import * as React from "react";

/**
 * Fluid Functionalism motion tiers.
 *
 * Tier value = ENTER transition (critically damped spring); `.exit` = matching
 * EXIT transition (plain tween, one step quicker) for use as
 * `exit={{ opacity: 0, transition: fluidSprings.fast.exit }}`.
 *
 * Bigger element = slower tier. Never hand-write a duration - reach for a tier.
 * Bounce is always 0: no overshoot.
 */
export const fluidSprings = {
  fast: { type: "spring" as const, duration: 0.08, bounce: 0, exit: { duration: 0.06 } },
  moderate: { type: "spring" as const, duration: 0.16, bounce: 0, exit: { duration: 0.12 } },
  slow: { type: "spring" as const, duration: 0.24, bounce: 0, exit: { duration: 0.16 } },
} as const;

export type FluidMotionTier = (typeof fluidSprings)[keyof typeof fluidSprings];

export const exitFallbackMs = (tier: { exit: { duration: number } }) =>
  Math.round(tier.exit.duration * 1000) + 100;

export function fluidFadeMotion(open: boolean, tier: FluidMotionTier) {
  return {
    animate: { opacity: open ? 1 : 0 },
    initial: { opacity: 0 },
    transition: open ? tier : tier.exit,
  } as const;
}

export function fluidScaleFadeMotion(open: boolean, tier: FluidMotionTier) {
  return {
    animate: { opacity: open ? 1 : 0, scale: open ? 1 : 0.97 },
    initial: { opacity: 0, scale: 0.97 },
    transition: open ? tier : tier.exit,
  } as const;
}

export function omitMotionConflictingProps<
  TProps extends {
    onAnimationStart?: unknown;
    onDrag?: unknown;
    onDragEnd?: unknown;
    onDragStart?: unknown;
  },
>(
  props: TProps,
): Omit<TProps, "onAnimationStart" | "onDrag" | "onDragEnd" | "onDragStart"> {
  const { onAnimationStart, onDrag, onDragEnd, onDragStart, ...motionProps } =
    props;

  void onAnimationStart;
  void onDrag;
  void onDragEnd;
  void onDragStart;

  return motionProps;
}

type PreventUnmountOnCloseDetails = {
  preventUnmountOnClose: () => void;
};

type FluidOverlayActionsRef<Actions> = React.RefObject<Actions | null>;

type FluidOverlayOpenChange<Details> = (
  open: boolean,
  eventDetails: Details,
) => void;

type UseFluidOverlayRootOptions<Actions, Details> = {
  actionsRef?: FluidOverlayActionsRef<Actions>;
  defaultOpen?: boolean;
  onOpenChange?: FluidOverlayOpenChange<Details>;
  open?: boolean;
};

type UseFluidOverlayRootResult<Actions, Details> = {
  actionsRef: FluidOverlayActionsRef<Actions>;
  open: boolean;
  rootProps: {
    actionsRef: FluidOverlayActionsRef<Actions>;
    open: boolean;
    onOpenChange: FluidOverlayOpenChange<Details>;
  };
};

function hasPreventUnmountOnClose(
  eventDetails: unknown,
): eventDetails is PreventUnmountOnCloseDetails {
  if (typeof eventDetails !== "object" || eventDetails === null) {
    return false;
  }

  const details = eventDetails as { preventUnmountOnClose?: unknown };
  return typeof details.preventUnmountOnClose === "function";
}

export function useFluidOverlayRoot<Actions, Details>({
  actionsRef,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
}: UseFluidOverlayRootOptions<Actions, Details>): UseFluidOverlayRootResult<
  Actions,
  Details
> {
  const internalActionsRef = React.useRef<Actions | null>(null);
  const resolvedActionsRef = actionsRef ?? internalActionsRef;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const handleOpenChange = React.useCallback<FluidOverlayOpenChange<Details>>(
    (nextOpen, eventDetails) => {
      if (!nextOpen && hasPreventUnmountOnClose(eventDetails)) {
        eventDetails.preventUnmountOnClose();
      }

      if (controlledOpen === undefined) {
        setUncontrolledOpen(nextOpen);
      }

      onOpenChange?.(nextOpen, eventDetails);
    },
    [controlledOpen, onOpenChange],
  );

  const rootProps = React.useMemo(
    () => ({
      actionsRef: resolvedActionsRef,
      open,
      onOpenChange: handleOpenChange,
    }),
    [handleOpenChange, open, resolvedActionsRef],
  );

  return React.useMemo(
    () => ({
      actionsRef: resolvedActionsRef,
      open,
      rootProps,
    }),
    [open, resolvedActionsRef, rootProps],
  );
}

export function useFluidExitUnmount(
  open: boolean,
  tier: FluidMotionTier,
  unmount: () => void,
) {
  const hasOpenedRef = React.useRef(open);
  const unmountRef = React.useRef(unmount);

  React.useEffect(() => {
    unmountRef.current = unmount;
  }, [unmount]);

  React.useEffect(() => {
    if (open) {
      hasOpenedRef.current = true;
      return;
    }

    if (!hasOpenedRef.current) {
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      unmountRef.current();
    }, exitFallbackMs(tier));

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [open, tier]);
}

export const modalContentMotion = {
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  initial: { opacity: 0, y: 6 },
} as const;
